import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Video, Shuffle, ShieldAlert, LogIn, LogOut, ShieldCheck, History, Smartphone } from 'lucide-react';
import TRTC from 'trtc-sdk-v5';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LanguageToggle } from '../components/LanguageToggle';
import { generateRoomId } from '../lib/roomId';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { JOIN, HOME } from '@/constants/testIds';

export default function JoinRoom() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [roomId, setRoomId] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [error, setError] = useState('');
  const [envCheck, setEnvCheck] = useState(null);

  useEffect(() => {
    TRTC.isSupported()
      .then((result) => setEnvCheck(result?.detail || null))
      .catch(() => setEnvCheck(null));
  }, []);

  const isCreating = !roomId.trim();

  const handleJoin = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('home.nameRequiredError'));
      return;
    }
    if (isCreating && !user) {
      setError(t('home.loginRequiredError'));
      return;
    }
    const finalRoom = (roomId.trim() || generateRoomId()).toUpperCase();
    navigate(`/room/${finalRoom}`, { state: { name: name.trim(), title: isCreating ? (meetingTitle.trim() || t('home.titlePlaceholder')) : undefined } });
  };

  return (
    <div data-testid={JOIN.container} className="flex h-screen w-screen items-center overflow-hidden bg-[#F8FAFC] px-8 sm:px-16 lg:px-28">
      <div className="absolute right-8 top-6 flex items-center gap-4 sm:right-16">
        <Link
          data-testid="home-mobile-download-link"
          to="/mobile"
          className="flex items-center gap-1 font-body text-sm text-slate-500 hover:text-slate-900"
        >
          <Smartphone className="h-4 w-4" /> 手机版
        </Link>
        <LanguageToggle />
        {user === null ? null : user ? (
          <>
            <span data-testid={HOME.navUserLabel} className="font-body text-sm text-slate-500">
              {user.name}
              {user.role === 'admin' && <span className="ml-1 text-amber-500">{t('home.adminBadge')}</span>}
            </span>
            {user.role === 'admin' ? (
              <Link data-testid={HOME.navAdminLink} to="/admin" className="flex items-center gap-1 font-body text-sm text-slate-500 hover:text-slate-900">
                <ShieldCheck className="h-4 w-4" /> {t('home.adminPanel')}
              </Link>
            ) : (
              <Link data-testid={HOME.navMyMeetingsLink} to="/my-meetings" className="flex items-center gap-1 font-body text-sm text-slate-500 hover:text-slate-900">
                <History className="h-4 w-4" /> {t('home.myMeetings')}
              </Link>
            )}
            <button
              data-testid={HOME.navLogoutBtn}
              onClick={() => logout()}
              className="flex items-center gap-1 font-body text-sm text-slate-500 hover:text-slate-900"
            >
              <LogOut className="h-4 w-4" /> {t('home.logout')}
            </button>
          </>
        ) : (
          <Link data-testid={HOME.navLoginLink} to="/login" className="flex items-center gap-1 font-body text-sm text-slate-500 hover:text-slate-900">
            <LogIn className="h-4 w-4" /> {t('home.login')}
          </Link>
        )}
      </div>
      <div className="grid w-full max-w-5xl items-center gap-16 lg:grid-cols-[1.1fr_1fr]">
        <div className="max-w-xl">
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {t('home.title')}
          </h1>
          {envCheck && !envCheck.isH264EncodeSupported && (
            <div data-testid="env-warning-h264" className="mt-6 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <p className="font-body text-sm text-amber-800">{t('home.h264Warning')}</p>
            </div>
          )}
        </div>

        <form
          onSubmit={handleJoin}
          className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
        >
          <label className="mb-2 block font-body text-sm text-slate-500">{t('home.yourNameLabel')}</label>
          <Input
            data-testid={JOIN.nameInput}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
            placeholder={t('home.namePlaceholder')}
            className="mb-5 h-12 border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#3B82F6]"
          />

          <label className="mb-2 block font-body text-sm text-slate-500">{t('home.roomIdLabel')}</label>
          <div className="mb-2 flex gap-2">
            <Input
              data-testid={JOIN.roomInput}
              value={roomId}
              onChange={(e) => setRoomId(e.target.value.toUpperCase())}
              placeholder={t('home.roomIdPlaceholder')}
              className="h-12 flex-1 border-slate-200 bg-slate-50 font-mono text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#3B82F6]"
            />
            <Button
              type="button"
              data-testid={JOIN.generateRoomBtn}
              variant="outline"
              onClick={() => setRoomId(generateRoomId())}
              className="h-12 shrink-0 border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
            >
              <Shuffle className="h-4 w-4" />
            </Button>
          </div>

          {isCreating && user && (
            <div className="mt-3">
              <label className="mb-2 block font-body text-sm text-slate-500">{t('home.titleLabel')}</label>
              <Input
                data-testid={HOME.titleInput}
                value={meetingTitle}
                onChange={(e) => setMeetingTitle(e.target.value)}
                placeholder={t('home.titlePlaceholder')}
                className="h-12 border-slate-200 bg-slate-50 text-slate-900 focus-visible:ring-[#3B82F6]"
              />
            </div>
          )}

          {isCreating && !user && (
            <p data-testid={HOME.loginRequiredNotice} className="mt-3 font-body text-xs text-amber-600">
              {t('home.loginRequiredNotice')}
            </p>
          )}

          {error && (
            <p data-testid={JOIN.errorMessage} className="mb-2 mt-2 font-body text-sm text-red-400">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid={JOIN.joinBtn}
            className="mt-6 h-12 w-full rounded-full bg-[#3B82F6] font-body text-base font-medium text-white transition-transform duration-200 hover:scale-[1.02] hover:bg-[#3B82F6]/90"
          >
            <Video className="mr-2 h-4 w-4" /> {isCreating ? t('home.createAndJoin') : t('home.joinCall')}
          </Button>
        </form>
      </div>
    </div>
  );
}
